<template>
  <div
    data-test="graphics-textSettings"
  >
    <SidebarSettingsGroup
      v-if="!element.data?.noRemap && !editingMultiple"
      title="Content"
      collapsible
      :element-type="element.type"
      data-test="graphics-textSettings-content"
    >
      <div class="sidebar-form-group">
        <div class="text-settings-header flex flex-center-all margin-bottom-sm">
          <p class="sidebar-form-label flex-grow">
            Text
          </p>
        </div>
        <!-- Edit contents of text element, or bind to spreadsheet with vars -->
        <div
          v-if="!checkMapped('text') || labelOverride"
          class="flex margin-bottom-sm"
          data-test="graphics-textSettings-text"
        >
          <div
            v-once
            id="textContentField"
            ref="editor"
            class="contenteditable-editor"
            contenteditable
            spellcheck="false"
            data-test="graphics-textSettings-setText"
            data-entity="graphics-textSettings-setText"
            @input="onChangeText($event, true)"
            @blur="onBlur"
            @focus="isEditingText = true"
            @keydown.stop
            v-html="text"
          />
        </div>

        <p
          v-if="checkMapped('text') && !labelOverride"
          class="sidebar-form-label margin-bottom-sm"
        >
          From Data Provider
        </p>

        <map-data-control
          v-if="dataProvider"
          :data-provider="dataProvider"
          :scroll-offset="scrollOffset"
          :element="element"
          property="text"
          :mapped="checkMapped('text')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-textSettings-styleAndAnimation"
    >
      <div class="sidebar-form-group">
        <div class="text-setting-header">
          <label class="sidebar-form-label"> Font </label>
        </div>

        <font-dropdown
          :custom-fonts="customFontsArray"
          :element="element"
        />

        <div class="settings-row settings-row-xs">
          <label class="sidebar-form-label">Text Style</label>
          <p
            v-if="element.data?.required"
            class="info-box info"
            data-test="graphics-textSettings-requiredText"
          >
            <span class="fa fa-info-circle" />
            Required text has a minimum size of {{ minRequiredFontSize }}px.
          </p>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="fontSize"
              :rules="`${editingMultiple ? '' : 'required|'}numeric|min:0`"
              :disabled="fitText"
              type="number"
              name="fontSize"
              :min="element.data?.required ? minRequiredFontSize : 0"
              :class="{ 'is-invalid': validationErrors['fontSize'] }"
              data-test="graphics-textSettings-setFontSize"
              data-entity="graphics-textSettings-setFontSize"
            />
            <label class="sidebar-input-sub-label">Size</label>
          </div>

          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="letterSpacing"
              :rules="`${editingMultiple ? '' : 'required|'}numeric|min:0`"
              type="number"
              name="letterSpacing"
              min="0"
              :class="{ 'is-invalid': validationErrors['letterSpacing'] }"
              data-test="graphics-textSettings-setLetterSpacing"
              data-entity="graphics-textSettings-setLetterSpacing"
            />
            <label class="sidebar-input-sub-label">Letter spacing</label>
          </div>

          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="lineHeight"
              :rules="`${editingMultiple ? '' : 'required|'}decimal|min:0`"
              type="number"
              name="lineHeight"
              min="0"
              step="0.1"
              :class="{ 'is-invalid': validationErrors['lineHeight'] }"
              data-test="graphics-textSettings-setLineHeight"
              data-entity="graphics-textSettings-setLineHeight"
            />
            <label class="sidebar-input-sub-label">Line Spacing</label>
          </div>
        </div>
        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <div class="colorpicker">
              <colorpicker
                v-model="color"
                :allow-alpha="!element.data?.required"
                :is-mapped="checkMapped('color')"
              />
            </div>
            <label class="sidebar-input-sub-label">
              Color
              <span
                v-if="checkMapped('color')"
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
                data-test="graphics-textSettings-colorMappedLock"
              >
                <span class="fa fa-lock mapped-lock" />
              </span>
            </label>
          </div>
        </div>
        <template v-if="showDataMapControls">
          <p
            v-if="isTextStyleMapped"
            class="sidebar-form-label margin-bottom-sm"
            data-test="graphics-textSettings-colorMapped"
          >
            From Data Provider
          </p>
          <map-data-control
            :data-provider="dataProvider"
            :scroll-offset="scrollOffset"
            :element="element"
            property="color"
            :mapped="checkMapped('color')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
        </template>

        <!--<opacity-settings :element="element"></opacity-settings>-->
        <div class="settings-row settings-row-xs no-tooltip auto-size">
          <div class="settings-col-12 settings-col-xs">
            <label
              class="checkbox"
              :class="{ 'checkbox-disabled': autoSizeDisabled }"
            >
              Autosize Text
              <input
                v-model="fitText"
                type="checkbox"
                :disabled="autoSizeDisabled"
                data-test="graphics-textSettings-toggleAutoSize"
                data-entity="graphics-textSettings-toggleAutoSize"
                @change="setAutosize()"
              >
              <div
                aria-label="Autosize Text"
                class="checkmark"
              >
                <span class="checkmark-tick fa-solid fa-check" />
              </div>
            </label>
          </div>
        </div>
        <div
          v-show="fitText"
          class="settings-row settings-row-xs no-tooltip max-size"
        >
          <div class="settings-col-8 settings-col-xs">
            <label class="checkbox">Max Text Size
              <input
                ref="maxCheckbox"
                v-model="maxSizeText"
                type="checkbox"
                data-test="graphics-textSettings-toggleMaxSize"
                data-entity="graphics-textSettings-toggleMaxSize"
                @change="onMaxFontSizeUpdate"
              />
              <div
                aria-label="Autosize Text"
                class="checkmark"
              >
                <span class="checkmark-tick fa-solid fa-check"></span>
              </div>
            </label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              id="maxSizeText"
              v-model.number="maxSizeTextStyle"
              :rules="maxSizeText ? 'required|numeric|min_value:0' : 'numeric|min_value:0'"
              type="number"
              name="maxSizeText"
              :disabled="!maxSizeText"
              min="0"
              :class="{ 'is-invalid': validationErrors['maxSizeText'] }"
              data-test="graphics-textSettings-setMaxSize"
              data-entity="graphics-textSettings-setMaxSize"
              @input.stop.prevent="onChange"
            />
          </div>
        </div>

        <div class="settings-row settings-row-xs text-styles">
          <div class="settings-col-12 settings-col-xs transforms">
            <button
              aria-label="Bold"
              class="btn btn-sm btn-icon"
              :class="getFontWeightBtnClass"
              data-test="graphics-textSettings-bold"
              data-entity="graphics-textSettings-bold"
              @click="setFontWeight"
            >
              <span class="fa-solid fa-bold" />
            </button>
            <button
              aria-label="Italic"
              class="btn btn-sm btn-icon"
              :class="getFontStyleBtnClass"
              data-test="graphics-textSettings-italic"
              data-entity="graphics-textSettings-italic"
              @click="setFontStyle"
            >
              <span class="fa-solid fa-italic" />
            </button>
            <button
              aria-label="Underline"
              class="btn btn-sm btn-icon"
              :class="getUnderlineBtnClass"
              data-test="graphics-textSettings-underline"
              data-entity="graphics-textSettings-underline"
              @click="setUnderlineStyle"
            >
              <span class="fa-solid fa-underline" />
            </button>
            <button
              aria-label="Strikethrough"
              class="btn btn-sm btn-icon"
              :class="getStrikethroughBtnClass"
              data-test="graphics-textSettings-strikethrough"
              data-entity="graphics-textSettings-strikethrough"
              @click="setStrikethroughStyle"
            >
              <span class="fa-solid fa-strikethrough" />
            </button>
            <button
              aria-label="Uppercase"
              class="btn btn-sm btn-icon"
              :class="getTransformBtnClass"
              data-test="graphics-textSettings-uppercase"
              data-entity="graphics-textSettings-uppercase"
              @click="setTransformStyle"
            >
              <span class="fa-solid fa-font" />
            </button>
          </div>
        </div>

        <div class="settings-row settings-row-xs">
          <label class="sidebar-form-label">Padding</label>
          <SpacingSettings
            :top="paddingTop"
            :right="paddingRight"
            :bottom="paddingBottom"
            :left="paddingLeft"
            @update:top="paddingTop = $event"
            @update:right="paddingRight = $event"
            @update:bottom="paddingBottom = $event"
            @update:left="paddingLeft = $event"
          />
        </div>

        <label class="sidebar-form-label">Text Layout</label>

        <div class="settings-row settings-row-xs text-styles">
          <div class="settings-col-12 settings-col-xs">
            <div class="btn-group text-box">
              <!-- auto scale is disabled if auto-size is selected -->
              <button
                v-for="textOption in textFlow"
                :key="textOption.id"
                type="button"
                :class="[
                  'btn btn-sm',
                  {
                    selected: textFlowOption === textOption.id,
                  },
                ]"
                :aria-label="textOption.tooltip"
                :aria-pressed="textFlowOption === textOption.id"
                :data-test="`graphics-textSettings-${textOption.id}`"
                :data-entity="`graphics-textSettings-${textOption.id}`"
                @click="setTextFlow(textOption.id)"
              >
                <span :class="textOption.icon" />
              </button>
            </div>
          </div>
        </div>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <div class="btn-group text-box">
              <button
                v-for="textOption in textBox"
                :key="textOption.id"
                type="button"
                :disabled="textOption.id === 2 && autoSizeTextboxDisabled"
                :class="[
                  {
                    selected: textBoxOption === textOption.id,
                  },
                ]"
                class="btn btn-sm"
                :aria-label="textOption.tooltip"
                :aria-pressed="textBoxOption === textOption.id"
                :data-test="`graphics-textSettings-${textOption.id}`"
                :data-entity="`graphics-textSettings-${textOption.id}`"
                @click="setTextBox(textOption.id)"
              >
                <span :class="textOption.icon" />
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="!checkMapped('text')"
          class="settings-row settings-row-xs no-tooltip"
        >
          <div class="text-setting-header">
            <label class="sidebar-form-label">Character Count</label>
          </div>
          <div class="settings-col-12 settings-col-xs">
            <label class="checkbox">Limit Characters
              <input
                v-model="limitCharacters"
                type="checkbox"
                data-test="graphics-textSettings-setLimitCharacters"
                data-entity="graphics-textSettings-setLimitCharacters"
                @change="limitCharacterCount()"
              />
              <div
                aria-label="Limit Characters"
                class="checkmark"
              >
                <span class="checkmark-tick fa-solid fa-check"></span>
              </div>
            </label>
          </div>
          <div class="settings-col-6 settings-col-xs">
            <validation-field as="input"
              v-model.number="maxCharacterLength"
              rules="numeric|min:0"
              type="number"
              name="maxCharacterLength"
              min="0"
              data-test="graphics-textSettings-setMaxCharacterLength"
              data-entity="graphics-textSettings-setMaxCharacterLength"
            />
            <label class="sidebar-input-sub-label">Max Length</label>
          </div>
          <div class="settings-col-6 settings-col-xs">
            <p class="char-count">
              {{ characterCount }}
            </p>
            <p class="sidebar-input-sub-label">
              Count
            </p>
          </div>
        </div>

        <label class="sidebar-form-label">Text Alignment</label>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <div class="btn-group">
              <button
                v-for="align in alignments"
                :key="align"
                type="button"
                class="btn btn-sm"
                :class="[{ selected: textAlign === align }]"
                :aria-label="alignmentTooltips[align]"
                :data-test="`graphics-textSettings-${align}`"
                :data-entity="`graphics-textSettings-${align}`"
                @click="setAlignment(align)"
              >
                <span
                  class="fa-solid"
                  :class="'fa-align-' + align"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <div class="btn-group vertical-alignments">
              <button
                v-for="align in verticalAlignments"
                :key="align"
                type="button"
                :class="[{ selected: verticalAlign === align }]"
                class="btn btn-sm"
                :data-test="`graphics-textSettings-${align}`"
                :data-entity="`graphics-textSettings-${align}`"
                @click="setVerticalAlignment(align)"
              >
                <span
                  :class="`fak fa-text-align-${align}`"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <hr class="sidebar-divider">

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Text shadow
          <input
            v-model="textShadow"
            type="checkbox"
            data-test="graphics-textSettings-toggleTextShadow"
            data-entity="graphics-textSettings-toggleTextShadow"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="textShadow"
          class="settings-row settings-row-xs"
        >
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowX"
              rules="required|integer"
              type="number"
              name="textShadowX"
              :class="{ 'is-invalid': validationErrors['textShadowX'] }"
              data-test="graphics-textSettings-setTextShadowX"
              data-entity="graphics-textSettings-setTextShadowX"
            />
            <label class="sidebar-input-sub-label">X</label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowY"
              rules="required|integer"
              type="number"
              name="textShadowY"
              :class="{ 'is-invalid': validationErrors['textShadowY'] }"
              data-test="graphics-textSettings-setTextShadowY"
              data-entity="graphics-textSettings-setTextShadowY"
            />
            <label class="sidebar-input-sub-label">Y</label>
          </div>
          <div class="settings-col-4 settings-col-xs">
            <validation-field as="input"
              v-model.number="textShadowBlur"
              rules="required|integer"
              type="number"
              name="textShadowBlur"
              :class="{ 'is-invalid': validationErrors['textShadowBlur'] }"
              data-test="graphics-textSettings-setTextShadowBlur"
              data-entity="graphics-textSettings-setTextShadowBlur"
            />
            <label class="sidebar-input-sub-label">Blur</label>
          </div>
          <div class="settings-col settings-col-xs colorpicker margin-bottom-sm">
            <colorpicker
              v-model="textShadowColor"
              :is-mapped="checkMapped('textShadowColor')"
            />
            <label class="sidebar-input-sub-label">
              Color
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span
                  v-if="checkMapped('textShadowColor')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </label>
          </div>

          <!-- TODO: remove once sheets can have more than one thing mapped -->
          <template v-if="showDataMapControls">
            <p
              v-if="checkMapped('textShadowColor')"
              class="sidebar-form-label margin-bottom-sm"
            >
              From Data Provider
            </p>
            <map-data-control
              :data-provider="dataProvider"
              :scroll-offset="scrollOffset"
              :element="element"
              property="textShadowColor"
              :mapped="checkMapped('textShadowColor')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
          </template>
        </div>
      </div>

      <hr class="sidebar-divider">

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Background color
          <input
            v-model="background"
            type="checkbox"
            data-test="graphics-textSettings-toggleBackground"
            data-entity="graphics-textSettings-toggleBackground"
          >
          <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="background"
          class="settings-row settings-row-xs"
        >
          <label
            class="radio inline-radio"
            for="solid"
          >Solid
            <input
              id="solid"
              v-model="fillStyle"
              type="radio"
              value="solid"
              data-test="graphics-textSettings-fillStyleSolid"
            >
            <div class="checkmark" />
          </label>
          <label
            class="radio inline-radio"
            for="gradient"
          >Gradient
            <input
              id="gradient"
              v-model="fillStyle"
              type="radio"
              value="gradient"
              data-test="graphics-textSettings-fillStyleGradient"
            >
            <div class="checkmark" />
          </label>

          <div
            v-if="fillStyle == 'solid'"
            class="colorpicker text-center"
          >
            <colorpicker
              v-model="backgroundColor"
              :is-mapped="checkMapped('backgroundColor')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down"
              data-balloon-length="medium"
            >
              <span
                v-if="checkMapped('backgroundColor')"
                class="fa fa-lock mapped-lock"
              />
            </span>
          </div>

          <div
            v-else
            class="settings-row settings-row-xs"
          >
            <div class="settings-col-6 settings-col-xs">
              <div class="input-group">
                <validation-field as="input"
                  v-model.number="gradientDirection"
                  rules="required|numeric"
                  type="number"
                  name="gradientDirection"
                  :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                  data-test="graphics-textSettings-setGradientDirection"
                  data-entity="graphics-textSettings-setGradientDirection"
                />
                <span class="input-group-addon symbol">°</span>
              </div>
            </div>
            <div class="settings-col-3 settings-col-xs colorpicker text-center">
              <colorpicker
                v-model="gradientColorOne"
                :is-mapped="checkMapped('gradientColorOne')"
              />
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down"
                data-balloon-length="medium"
              >
                <span
                  v-if="checkMapped('gradientColorOne')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </div>
            <div class="settings-col-3 settings-col-xs colorpicker text-center">
              <colorpicker
                v-model="gradientColorTwo"
                :is-mapped="checkMapped('gradientColorTwo')"
              />
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down-right"
                data-balloon-length="medium"
              >
                <span
                  v-if="checkMapped('gradientColorTwo')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </div>
          </div>

          <!-- TODO: remove once sheets can have more than one thing mapped -->
          <div
            v-if="showDataMapControls"
            class="margin-top-sm"
            data-test="graphics-textSettings-backgroundMapped"
          >
            <p
              v-if="isBackgroundColorMapped"
              class="sidebar-form-label margin-top-sm"
            >
              From Data Provider
            </p>

            <map-data-control
              v-if="fillStyle == 'solid'"
              :data-provider="dataProvider"
              :element="element"
              :scroll-offset="scrollOffset"
              property="backgroundColor"
              :mapped="checkMapped('backgroundColor')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
            <map-data-control
              v-if="fillStyle == 'gradient'"
              :data-provider="dataProvider"
              :element="element"
              :scroll-offset="scrollOffset"
              property="gradientColorOne"
              :mapped="checkMapped('gradientColorOne')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
            <map-data-control
              v-if="fillStyle == 'gradient'"
              :data-provider="dataProvider"
              :element="element"
              :scroll-offset="scrollOffset"
              property="gradientColorTwo"
              :mapped="checkMapped('gradientColorTwo')"
              @update-map="mapToField"
              @clear-map="clearMapping"
            />
          </div>
        </div>
      </div>

      <hr class="sidebar-divider">

      <animation-settings :element="element" />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EventBus from '../../utils/event-bus';
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import MapDataControl from './Data/MapDataControl.vue';
import FontDropdown from './FontDropdown.vue';
import SpacingSettings from './Spacing.vue';

export default {
  components: {
    AnimationSettings,
    FontDropdown,
    MapDataControl,
    SpacingSettings,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],

  data() {
    return {
      isEditingText: false,
      minRequiredFontSize: 15,

      alignmentTooltips: {
        left: 'Left',
        center: 'Center',
        right: 'Right',
        justify: 'Justify',
      },

      alignments: ['left', 'center', 'right', 'justify'],
      verticalAlignments: ['top', 'middle', 'bottom'],

      textBox: [
        {
          icon: 'fak fa-fixed-height-width',
          tooltip: 'Fixed size textbox',
          id: 1,
        },
        { icon: 'fak fa-auto-height-width', tooltip: 'Autosize textbox', id: 2 },
      ],

      textFlow: [
        { icon: 'fak fa-no-wrap-text', tooltip: 'No wrap', id: 1 },
        { icon: 'fak fa-wrap-text', tooltip: 'Wrap text', id: 2 },
        // { icon: 'icon-dynamic-text', tooltip: 'Autosize text', id: 3 }
      ],
    };
  },

  computed: {
    ...mapGetters(['customFontsArray', 'customFonts']),
    text: {
      get() {
        if (this.isMapped('text')) {
          return this.getDataValue(this.element.id, 'text');
        }
        return this.getPropertyValue('text', '');
      },
      set(v) {
        this.updateOneElementValue('text', v);
        if (this.labelOverride) {
          // if sportradar, override label/name with text content
          const div = document.createElement('div');
          div.innerHTML = v;
          const textContent = div.textContent || '';
          this.updateOneElementValue('data.labelOverride', true);
          this.updateOneElementValue('name', textContent);
        }
      },
    },
    fontSize: {
      get() {
        return this.getPropertyValue('styles.fontSize');
      },
      set(v) {
        let value;
        if (v < this.minRequiredFontSize) {
          value = this.element.data?.required ? this.minRequiredFontSize : v;
        } else {
          value = v;
        }
        this.updateOneElementValue('styles.fontSize', value);
      },
    },
    letterSpacing: {
      get() {
        return this.getPropertyValue('styles.letterSpacing');
      },
      set(v) {
        this.updateOneElementValue('styles.letterSpacing', v);
      },
    },
    lineHeight: {
      get() {
        return this.getPropertyValue('styles.lineHeight', { fallbackValue: 1.25 }); // preserve old text elements
      },
      set(v) {
        this.updateOneElementValue('styles.lineHeight', v);
      },
    },
    color: {
      get() {
        return this.getPropertyValue('styles.color', { mappedPath: 'color' });
      },
      set(v) {
        this.updateOneElementValue('styles.color', v);
      },
    },
    fitText: {
      get() {
        return this.getPropertyValue('fitText');
      },
      set(v) {
        this.updateOneElementValue('fitText', v);
      },
    },
    maxSizeText: {
      get() {
        return this.getPropertyValue('maxSizeText');
      },
      set(v) {
        this.updateOneElementValue('maxSizeText', v);
      },
    },
    maxSizeTextStyle: {
      get() {
        return this.getPropertyValue('styles.maxSizeText');
      },
      set(v) {
        this.updateOneElementValue('styles.maxSizeText', v);
      },
    },
    limitCharacters: {
      get() {
        return this.getPropertyValue('limitCharacters');
      },
      set(v) {
        this.updateOneElementValue('limitCharacters', v);
      },
    },
    maxCharacterLength: {
      get() {
        return this.getPropertyValue('maxCharacterLength');
      },
      set(v) {
        this.updateOneElementValue('maxCharacterLength', v);
      },
    },
    textShadow: {
      get() {
        return this.getPropertyValue('textShadow');
      },
      set(v) {
        this.updateOneElementValue('textShadow', v);
      },
    },
    textShadowX: {
      get() {
        return this.getPropertyValue('textShadowX');
      },
      set(v) {
        this.updateOneElementValue('textShadowX', v);
      },
    },
    textShadowY: {
      get() {
        return this.getPropertyValue('textShadowY');
      },
      set(v) {
        this.updateOneElementValue('textShadowY', v);
      },
    },
    textShadowBlur: {
      get() {
        return this.getPropertyValue('textShadowBlur');
      },
      set(v) {
        this.updateOneElementValue('textShadowBlur', v);
      },
    },
    textShadowColor: {
      get() {
        return this.getPropertyValue('textShadowColor', { mappedPath: 'textShadowColor' });
      },
      set(v) {
        this.updateOneElementValue('textShadowColor', v);
      },
    },
    background: {
      get() {
        return this.getPropertyValue('background');
      },
      set(v) {
        this.updateOneElementValue('background', v);
      },
    },
    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle');
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { mappedPath: 'backgroundColor' });
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { mappedPath: 'gradientColorOne' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { mappedPath: 'gradientColorTwo' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },

    paddingTop: {
      get() {
        return this.getPropertyValue('padding.top', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('padding.top', v);
        this.onChange();
      },
    },
    paddingRight: {
      get() {
        return this.getPropertyValue('padding.right', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('padding.right', v);
        this.onChange();
      },
    },
    paddingBottom: {
      get() {
        return this.getPropertyValue('padding.bottom', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('padding.bottom', v);
        this.onChange();
      },
    },
    paddingLeft: {
      get() {
        return this.getPropertyValue('padding.left', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('padding.left', v);
        this.onChange();
      },
    },

    getFontWeightBtnClass() {
      return this.getPropertyValue('styles.fontWeight') === 'bold'
        ? 'btn-style-a'
        : 'btn-style-d';
    },

    getFontStyleBtnClass() {
      return this.getPropertyValue('styles.fontStyle') === 'italic'
        ? 'btn-style-a'
        : 'btn-style-d';
    },

    getUnderlineBtnClass() {
      if (this.getPropertyValue('styles.textDecoration') === 'underline') {
        return 'btn-style-a';
      }
      return 'btn-style-d';
    },

    getStrikethroughBtnClass() {
      if (this.getPropertyValue('styles.textDecoration') === 'line-through') {
        return 'btn-style-a';
      }
      return 'btn-style-d';
    },

    getTransformBtnClass() {
      return this.getPropertyValue('styles.textTransform') === 'uppercase'
        ? 'btn-style-a'
        : 'btn-style-d';
    },

    characterCount() {
      const div = document.createElement('div');
      div.innerHTML = this.element.text;
      return div.textContent.length;
    },

    autoSizeDisabled() {
      return this.textBoxOption === 2;
    },

    autoSizeTextboxDisabled() {
      return this.fitText || this.checkMapped('text');
    },

    plan() {
      return this.$store.state.user.plan;
    },

    fontLabelTooltip() {
      return this.plan.permissions.custom_fonts
        ? 'Font Uploader is here! Now you can upload your very own fonts. Click the dropdown below to get started.'
        : 'Font Uploader is here! This feature requires a Premium or Ultimate package.';
    },

    isTextStyleMapped() {
      return this.checkMapped('color');
    },

    textFlowOption() {
      return this.getPropertyValue('styles.textFlowOption');
    },
    textBoxOption() {
      return this.getPropertyValue('styles.textBoxOption');
    },
    textAlign() {
      return this.getPropertyValue('styles.textAlign');
    },
    verticalAlign() {
      return this.getPropertyValue('verticalAlign');
    },
  },

  watch: {
    text(val) {
      if (!this.isEditingText) {
        if (this.$refs?.editor) {
          this.$refs.editor.innerHTML = val;
        }
      }
    },
  },

  mounted() {
    if (this.$refs.editor) {
      this.$refs.editor.addEventListener('paste', this.onPaste);
    }
  },

  beforeUnmount() {
    // Just in case change hasn't been saved, save now
    if (!this.dataProvider) {
      clearTimeout(this.textChangeTimeout);
    }

    if (this.$refs.editor) {
      this.$refs.editor.removeEventListener('paste', this.onPaste);
    }
  },

  methods: {
    onPaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertHTML', false, text);
    },

    onClick(e) {
      // close dropdown if click outside
      const { dataProviderDropdown } = this.$refs;
      if (
        dataProviderDropdown
        && !dataProviderDropdown.$el.contains(e.target)
      ) {
        this.showInjectDropdown = false;
      }
    },

    onMaxFontSizeUpdate() {
      if (this.maxSizeText) {
        this.maxSizeTextStyle = this.fontSize;
      } else {
        this.maxSizeTextStyle = 0;
      }
      EventBus.emit('text-adjusted');
    },

    onChangeText(e, debounce) {
      clearTimeout(this.textChangeTimeout);
      this.textChangeTimeout = setTimeout(
        () => {
          const text = this.sanitizeHtml(e.target.innerHTML);
          this.text = text;
        },
        debounce ? 500 : 0,
      );
    },

    sanitizeHtml(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('span[style]').forEach((span) => {
        span.replaceWith(...span.childNodes);
      });
      return div.innerHTML;
    },

    onBlur(e) {
      this.isEditingText = false;
      const text = e.target.textContent;
      const lineBreakCount = (text.match(/\n/g) || []).length;
      const len = text.length + lineBreakCount;
      // if it is a sportradar label, save the name as the text and labelOverride as true
      if (this.labelOverride) {
        this.updateOneElementValue('name', text); // used for assigning to the label
        this.updateOneElementValue('labelOverride', true); // used to demarcate that the element label is overridden
        this.updateOneElementValue('text', text); // used for rendering in the text editor
      }

      // enforce character length
      if (
        this.limitCharacters
        && len > this.maxCharacterLength
      ) {
        const nodes = [];
        e.target.childNodes.forEach((child) => {
          nodes.unshift(child);
        });

        const textCount = nodes.reduce(
          (accum, node) => accum + node.textContent.length,
          0,
        );
        let overflow = textCount - this.maxCharacterLength;

        for (let i = 0; i < nodes.length; i += 1) {
          const node = nodes[i];

          const t = node.textContent;
          if (t.length >= overflow) {
            // Slice up inner text and break out
            node.textContent = t.substr(0, t.length - overflow);
            break;
          }

          // Clear text of node, and continue to the next one
          overflow -= t.length;
          node.textContent = '';
        }
      }

      // Save can follow blur immediately; flush the pending input before it reads Vuex.
      clearTimeout(this.textChangeTimeout);
      this.text = this.sanitizeHtml(e.target.innerHTML);
    },

    onChange() {
      this.$nextTick(() => {
        this.resizeText();
      });
    },

    setAlignment(style) {
      this.updateOneElementValue('styles.textAlign', style);
    },

    setVerticalAlignment(style) {
      this.updateOneElementValue('verticalAlign', style);
    },

    setTextBox(id) {
      // disable ticker animation if not fixed size
      if (id !== 1) this.updateOneElementValue('ticker', false);

      this.updateOneElementValue('styles.textBoxOption', id);
      EventBus.emit('text-adjusted');
    },

    setTextFlow(id) {
      // disable ticker animation if not nowrap
      if (id !== 1) this.updateOneElementValue('ticker', false);

      this.updateOneElementValue('styles.textFlowOption', id);
      EventBus.emit('text-adjusted');
    },

    setAutosize() {
      EventBus.emit('text-adjusted');
    },

    setFontWeight() {
      if (this.getPropertyValue('styles.fontWeight', { emptyValue: 'normal' }) === 'normal') {
        this.updateOneElementValue('styles.fontWeight', 'bold');
      } else {
        this.updateOneElementValue('styles.fontWeight', 'normal');
      }
      EventBus.emit('text-adjusted');
    },

    setFontStyle() {
      if (this.getPropertyValue('styles.fontStyle', { emptyValue: 'normal' }) === 'normal') {
        this.updateOneElementValue('styles.fontStyle', 'italic');
      } else {
        this.updateOneElementValue('styles.fontStyle', 'normal');
      }
      EventBus.emit('text-adjusted');
    },

    setUnderlineStyle() {
      if (
        this.getPropertyValue('styles.textDecoration', { emptyValue: 'none' }) === 'none'
        || this.getPropertyValue('styles.textDecoration') === 'line-through'
      ) {
        this.updateOneElementValue('styles.textDecoration', 'underline');
      } else {
        this.updateOneElementValue('styles.textDecoration', 'none');
      }
      EventBus.emit('text-adjusted');
    },

    setStrikethroughStyle() {
      if (
        this.getPropertyValue('styles.textDecoration', { emptyValue: 'none' }) === 'none'
        || this.getPropertyValue('styles.textDecoration') === 'underline'
      ) {
        this.updateOneElementValue('styles.textDecoration', 'line-through');
      } else {
        this.updateOneElementValue('styles.textDecoration', 'none');
      }
      EventBus.emit('text-adjusted');
    },

    setTransformStyle() {
      if (this.getPropertyValue('styles.textTransform', { emptyValue: 'none' }) === 'none') {
        this.updateOneElementValue('styles.textTransform', 'uppercase');
      } else {
        this.updateOneElementValue('styles.textTransform', 'none');
      }
      EventBus.emit('text-adjusted');
    },

    limitCharacterCount() {
      if (this.limitCharacters) {
        this.updateOneElementValue('maxCharacterLength', this.characterCount);
      } else {
        this.updateOneElementValue('maxCharacterLength', 0);
      }
    },

    setFont(font) {
      this.updateOneElementValue('styles.fontFamily', font);
      EventBus.emit('text-adjusted');
    },

    resizeText() {
      EventBus.emit('resize-text');
    },
  },
};
</script>
